/**
 * TeamGoogleChatSetup - Per-team Google Chat OAuth configuration.
 * Allows team leads to authorize, test, and revoke their team's Google Chat connection.
 */
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ForumOutlined as ForumOutlinedIcon,
  CheckCircleOutlined as CheckCircleOutlinedIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  InfoOutlined as InfoOutlinedIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
  DeleteOutlined as DeleteOutlinedIcon,
  WifiTetheringOutlined as TestConnectionIcon,
} from '@mui/icons-material';
import { teamApi } from '@/services/api';
import type { TeamGoogleOAuthConfig } from '@/types';

interface TeamGoogleChatSetupProps {
  teamId: string;
}

type SnackbarState = {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info';
};

export default function TeamGoogleChatSetup({ teamId }: TeamGoogleChatSetupProps) {
  const queryClient = useQueryClient();

  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [popupBlockedDialogOpen, setPopupBlockedDialogOpen] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Track the OAuth popup window so we can detect when it closes
  const popupRef = useRef<Window | null>(null);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up the interval on unmount
  useEffect(() => {
    return () => {
      if (popupPollRef.current) clearInterval(popupPollRef.current);
    };
  }, []);

  const {
    data: oauthConfig,
    isLoading,
    refetch,
  } = useQuery<TeamGoogleOAuthConfig>({
    queryKey: ['team-google-oauth', teamId],
    queryFn: () => teamApi.getGoogleOAuthConfig(teamId).then((res) => res.data),
  });

  // Mutation: initialize OAuth flow
  const initMutation = useMutation({
    mutationFn: () => teamApi.initGoogleOAuth(teamId).then((res) => res.data),
    onSuccess: (data) => {
      // Open the authorization URL in a centered popup
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      const popup = window.open(
        data.authorization_url,
        'google-oauth-popup',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );
      popupRef.current = popup;

      // Poll until the popup closes, then refetch status
      if (popup) {
        if (popupPollRef.current) clearInterval(popupPollRef.current);
        popupPollRef.current = setInterval(() => {
          if (popup.closed) {
            if (popupPollRef.current) clearInterval(popupPollRef.current);
            popupPollRef.current = null;
            queryClient.invalidateQueries({ queryKey: ['team-google-oauth', teamId] });
          }
        }, 500);
      } else {
        // Popup was blocked by the browser — show the fallback dialog
        setPopupBlockedDialogOpen(true);
      }
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to start the authorization flow. Please try again.',
        severity: 'error',
      });
    },
  });

  // Mutation: clear / disconnect
  const clearMutation = useMutation({
    mutationFn: () => teamApi.clearGoogleOAuth(teamId).then((res) => res.data),
    onSuccess: () => {
      setDisconnectDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['team-google-oauth', teamId] });
      setSnackbar({
        open: true,
        message: 'Google Chat connection removed.',
        severity: 'success',
      });
    },
    onError: () => {
      setSnackbar({
        open: true,
        message: 'Failed to remove the connection. Please try again.',
        severity: 'error',
      });
    },
  });

  const handleTest = async () => {
    setIsTesting(true);
    try {
      const result = await teamApi.testGoogleOAuth(teamId).then((res) => res.data);
      setSnackbar({
        open: true,
        message: result.message,
        severity: result.status === 'success' ? 'success' : 'error',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Connection test failed. The backend may be unreachable.',
        severity: 'error',
      });
    } finally {
      setIsTesting(false);
    }
  };

  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderStatusSection = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }

    if (!oauthConfig || !oauthConfig.has_token) {
      return (
        <Stack spacing={2}>
          <Alert severity="info" icon={<InfoOutlinedIcon />}>
            No Google Chat connection configured. A team lead with access to this team&apos;s Google
            Chat space needs to authorize.
          </Alert>
          <Box>
            <Button
              variant="outlined"
              startIcon={
                initMutation.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <ForumOutlinedIcon />
                )
              }
              onClick={() => initMutation.mutate()}
              disabled={initMutation.isPending}
            >
              {initMutation.isPending ? 'Starting...' : 'Authorize Google Chat'}
            </Button>
          </Box>
        </Stack>
      );
    }

    // has_token = true, is_valid = false → expired / revoked
    if (oauthConfig.is_valid === false) {
      return (
        <Stack spacing={2}>
          <Alert severity="warning" icon={<WarningAmberOutlinedIcon />}>
            Google Chat connection expired or was revoked. Please re-authorize.
          </Alert>
          <Box>
            <Button
              variant="outlined"
              color="warning"
              startIcon={
                initMutation.isPending ? (
                  <CircularProgress size={16} />
                ) : (
                  <ForumOutlinedIcon />
                )
              }
              onClick={() => initMutation.mutate()}
              disabled={initMutation.isPending}
            >
              {initMutation.isPending ? 'Starting...' : 'Re-authorize'}
            </Button>
          </Box>
        </Stack>
      );
    }

    // has_token = true, is_valid = true or null → connected
    return (
      <Stack spacing={2}>
        <Alert severity="success" icon={<CheckCircleOutlinedIcon />}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="body2">
              Connected
              {oauthConfig.authorized_email ? ` as ${oauthConfig.authorized_email}` : ''}
            </Typography>
            {oauthConfig.authorized_at && (
              <Chip
                label={`Since ${formatDate(oauthConfig.authorized_at)}`}
                size="small"
                variant="outlined"
                color="success"
              />
            )}
          </Stack>
        </Alert>

        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            size="small"
            startIcon={isTesting ? <CircularProgress size={14} /> : <TestConnectionIcon />}
            onClick={handleTest}
            disabled={isTesting}
          >
            {isTesting ? 'Testing...' : 'Test Connection'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteOutlinedIcon />}
            onClick={() => setDisconnectDialogOpen(true)}
          >
            Disconnect
          </Button>
        </Stack>
      </Stack>
    );
  };

  return (
    <>
      <Card>
        <CardHeader
          avatar={<ForumOutlinedIcon color="primary" />}
          title="Google Chat Connection"
          subheader="Authorize this team's Google Chat space so the bot can read and reply to messages."
          action={
            <Tooltip title="Refresh status">
              <IconButton size="small" onClick={() => refetch()} disabled={isLoading}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent>{renderStatusSection()}</CardContent>
      </Card>

      {/* Disconnect confirmation dialog */}
      <Dialog
        open={disconnectDialogOpen}
        onClose={() => setDisconnectDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Disconnect Google Chat?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the OAuth token for this team. The bot will stop receiving messages
            from this team&apos;s Google Chat space until someone re-authorizes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisconnectDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => clearMutation.mutate()}
            disabled={clearMutation.isPending}
            startIcon={clearMutation.isPending ? <CircularProgress size={16} /> : undefined}
          >
            {clearMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Fallback dialog when the OAuth popup is blocked by the browser */}
      <Dialog
        open={popupBlockedDialogOpen}
        onClose={() => setPopupBlockedDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Open Authorization Page</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Your browser blocked the authorization popup. Click the button below to open the
            authorization page manually. Come back here once you have completed authorization.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPopupBlockedDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            endIcon={<OpenInNewIcon />}
            onClick={() => {
              if (initMutation.data?.authorization_url) {
                window.open(initMutation.data.authorization_url, '_blank');
              }
              setPopupBlockedDialogOpen(false);
            }}
          >
            Open Authorization Page
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for action feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
